import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { CountryPicker } from '../../../components/country-picker/country-picker';
import { CadastroPessoa } from '../cadastro-pessoa/cadastro-pessoa';
import { HabitosAlimentaresPessoa } from '../habitos-alimentares-pessoa/habitos-alimentares-pessoa';
import { PlanoAlimentarPessoa } from '../plano-alimentar-pessoa/plano-alimentar-pessoa';
import { MatDialog } from '@angular/material/dialog';
import { AdicionarPlano } from './dialogs/adicionar-plano/adicionar-plano';
import { PlanoAlimentar } from '../../../models/PlanoAlimentar';
import { PlanoAlimentarRefeicoes } from '../plano-alimentar-refeicoes/plano-alimentar-refeicoes';
import { unsavedChangesGuard } from '../../../guards/unsaved-changes-guard';
import { Pessoa } from '../../../models/Pessoa';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { TriagemCindi } from '../triagem-cindi/triagem-cindi';
import { AvaliacaoFisica } from '../avaliacao-fisica/avaliacao-fisica';
import { AuthService } from '../../../services/auth/auth.service';
import { AtividadeProposta } from '../atividade-proposta/atividade-proposta';
import { ConsultasExames } from '../consultas-exames/consultas-exames';

interface TelasPermissoes {
  idTela: number;
  visualizar: string[];
}

// IDs das linhas de cuidado
const ID_SOBREPESO_OBESIDADE = 2;
const ID_DOR_LOMBAR = 1;

// Telas visíveis apenas para Sobrepeso e Obesidade (ocultas para Dor Lombar)
const TELAS_APENAS_SOBREPESO = [2, 3, 4, 7]; // Hábitos alimentares, Plano alimentar, Atividade Proposta

@Component({
  selector: 'app-cadastrar-pessoa',
  templateUrl: './cadastrar-pessoa.html',
  styleUrls: ['./cadastrar-pessoa.scss'],
  imports: [
    FormsModule,
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatGridListModule,
    MatSelectModule,
    MatRadioModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    CadastroPessoa,
    HabitosAlimentaresPessoa,
    PlanoAlimentarPessoa,
    PlanoAlimentarRefeicoes,
    TriagemCindi,
    AvaliacaoFisica,
    AtividadeProposta,
    ConsultasExames
  ],
  providers: [provideNgxMask()],
})
export class CadastrarPessoa implements OnInit {
  telaExibida: number = 1;
  modoEdicao = false;
  caracteristicasPlano: any = null;
  planoSelecionado: PlanoAlimentar = new PlanoAlimentar();
  nomeCompleto = '';
  pessoaId!: number;
  linhaDeCuidadoId?: number;
  linhaDeCuidadoNome?: string;
  @ViewChild('planoCmp') planoRef?: PlanoAlimentarRefeicoes;

  constructor(
    private readonly route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private pessoaService: PessoasService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    this.verificarModo();
    await this.buscaNomePessoa();
  }

  podeExibirTela(idTela: number): boolean {
    const perfil = this.authService.getPerfil();
    const GESTOR = 'ROLE_GESTOR';
    const MASTER = 'ROLE_MASTER';
    const SAUDE = 'ROLE_SAUDE';

    const telaPermissoes: TelasPermissoes[] = [
      { idTela: 1, visualizar: [MASTER, SAUDE, GESTOR] },
      { idTela: 2, visualizar: [MASTER, SAUDE] },
      { idTela: 3, visualizar: [MASTER, SAUDE] },
      { idTela: 4, visualizar: [MASTER, SAUDE] },
      { idTela: 5, visualizar: [MASTER, SAUDE] },
      { idTela: 6, visualizar: [MASTER, SAUDE] },
      { idTela: 7, visualizar: [MASTER, SAUDE] },
      { idTela: 8, visualizar: [MASTER, SAUDE] },
    ];

    // Verifica permissão por perfil
    const tela = telaPermissoes.find((t) => t.idTela === idTela);
    if (!tela) return false;
    if (!tela.visualizar.includes(perfil)) return false;

    // Pessoa nova (cadastro) — exibe tudo
    if (!this.modoEdicao) return true;

    // Pessoa existente sem linha de cuidado vinculada — exibe só Cadastro
    if (this.modoEdicao && !this.linhaDeCuidadoId) {
      return idTela === 1;
    }

    // Para Dor Lombar, oculta as telas exclusivas de Sobrepeso
    if (
      this.linhaDeCuidadoId === ID_DOR_LOMBAR &&
      TELAS_APENAS_SOBREPESO.includes(idTela)
    ) {
      return false;
    }

    return true;
  }

  async buscaNomePessoa() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.modoEdicao = true;
      this.pessoaId = +id;
      const pessoa: Pessoa = await this.pessoaService.buscarPorId(
        this.pessoaId,
      );

      this.nomeCompleto = pessoa.nomeCompleto;
      const linha = pessoa.linhasDeCuidado?.[0];
      this.linhaDeCuidadoId = linha?.id;
      this.linhaDeCuidadoNome = linha?.nome;
    }
  }

  async verificarModo() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.modoEdicao = true;
    }
  }

  abrirModalSelecionarCaracteristicas() {
    const dialogRef = this.dialog.open(AdicionarPlano, {
      width: '700px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.caracteristicasPlano = result;
      }
    });
  }

  hasPendingChanges(): boolean {
    return this.planoRef?.hasPendingChanges?.() ?? false;
  }

  onPlanoSelecionado(plano: PlanoAlimentar) {
    this.planoSelecionado = plano;
    this.telaExibida = 4;
  }

  onPlanoSalvo(plano: PlanoAlimentar) {
    window.location.reload();
  }
}